import React, { useEffect, useState } from 'react';
import dayjs from 'dayjs';
import { Table, TableHead, TableRow, TableCell, TableBody, Box, IconButton } from '@mui/material';
import EditIcon from '@mui/icons-material/Edit';
import { useTheme } from '@mui/material/styles';
import { v4 as uuidv4 } from 'uuid';

import { DialogUI, InputUI, ButtonUI, AutoCompleteSelectUI, FormLabelUI } from '../../Interface';
import { DocumentType } from '../../../config/DocumentType';
import productionService from '../../../service/production.service';
import { cleanValues, generateAutocompleteItems } from '../../../utils/helper';
import addNotification from '../../../utils/addNotification';
import { NotificationType } from '../../../config';
import ProductionBatchOutDialog from './ProductionBatchOutDialog';
import ProductionReturnDialog from './ProductionReturnDialog';
import { InvManagementMethod } from '../../../config/itemGroup';
import { ProductionIssueDirection } from '../../../config/production';
import warehouseService from '../../../service/warehouse.service';

export const ProductionIssueDialog = ({
  open,
  onClose,
  selectedValues,
  setSelectedValues,
  setRefetchTable,
  direction,
  autocompleteData,
  autocompleteOptions,
}) => {
  const theme = useTheme();
  const ui = theme.palette.ui;
  const isIncoming = direction === ProductionIssueDirection.INCOMING;
  const dialogTitle = isIncoming ? 'Visszavét' : 'Kiadás';

  const [docLines, setDocLines] = useState([]);
  const [batchLine, setBatchLine] = useState(null);
  const [returnLine, setReturnLine] = useState(null);
  const [binManagedByWhs, setBinManagedByWhs] = useState({});
  const [targetWhsCode, setTargetWhsCode] = useState(null);
  const [targetBinId, setTargetBinId] = useState(null);

  const defaultBatchLine = {
    id: null,
    batchSn: '',
    lot: '',
    supplierId: '',
    expDate: null,
    mnfDate: null,
    batchAttribute1: '',
    batchAttribute2: '',
    comment: '',
    price: '',
    other: '',
    quantity: '',
  };

  const defaultDocumentLine = {
    lineStatus: 'C',
    itemCode: null,
    itemName: null,
    itemCodeInBp: null,
    qty: null,
    cplQty: null,
    opnQty: null,
    quantityUnitId: null,
    quantityUnitCode: null,
    quantityUnitName: null,
    whsCode: null,
    toWhsCode: null,
    toBinId: null,
    unitPrice: null,
    currencyCode: null,
    vatCode: null,
    vatPercent: null,
    vatValue: null,
    netPrice: null,
    grossPrice: null,
    dueDate: dayjs(),
    vatValueInOwnCur: null,
    netPriceInOwnCur: null,
    grossPriceInOwnCur: null,
    weightChangeNumber: null,
    weight: null,
    projectId: null,
    costCenterCode: null,
    itemReference: null,
    internalNote: '',
    bpNote: '',
    baseId: null,
    baseLine: null,
    baseDocnum: null,
    baseDocType: null,
    docLineBatchSn: [],
    docLineBin: [],
  };

  const defaultValues = {
    docTypeId: isIncoming ? DocumentType.ReturnFromProduction : DocumentType.IssueForProduction,
    docStatus: 'C',
    stornoId: null,
    docDate: dayjs(),
    issueDate: dayjs(),
    dueDate: dayjs(),
    companyId: null,
    companyCode: '',
    companyName: '',
    companyContactId: null,
    shortName: '',
    incotermCity: '',
    companyWarning: '',
    currencyCode: null,
    exchangeRate: null,
    paymentMethodId: null,
    reference: '',
    projectId: null,
    costCenterCode: null,
    salesOrPurchasePersonId: null,
    billingAdressId: null,
    billingCountry: '',
    billingProvince: '',
    billingPostCode: '',
    billingCity: '',
    billingStreetAddress: '',
    shippingAdressId: null,
    shippingCountry: '',
    shippingProvince: '',
    shippingPostCode: '',
    shippingCity: '',
    shippingStreetAddress: '',
    deliveryMethodId: null,
    incotermCode: null,
    whsCode: selectedValues.whsCode,
    toWhsCode: null,
    priceListId: null,
    langCode: null,
    hunTaxNumber: null,
    euTaxNumber: null,
    foreignTaxNumber: null,
    sumInDocCur: null,
    netInDocCur: null,
    VATInDocCur: null,
    roundingInDocCur: null,
    sumInOwnCur: null,
    netInOwnCur: null,
    VATInOwnCur: null,
    roundingInOwnCur: null,
    note: '',
    internalNote: '',
    docLine: [],
  };

  const toNumber = (value) => {
    const parsed = parseFloat(value);
    return Number.isNaN(parsed) ? 0 : parsed;
  };

  const buildIssueLines = () => {
    if (!selectedValues?.productionItem) {
      return addNotification({
        content: 'Nincs rögzítve tétel!',
        type: NotificationType.WARNING,
      });
    }

    if (!selectedValues?.whsCode) {
      return addNotification({
        content: 'Nincs rögzítve fej szintű raktár!',
        type: NotificationType.WARNING,
      });
    }
    const emptyLineWhs = selectedValues.productionItem.find((itm) => !itm.whsCode);
    if (emptyLineWhs) {
      addNotification({
        content: 'Nincs rögzítve sor szintű raktár!',
        type: NotificationType.WARNING,
      });
    }

    const newDocLines = selectedValues.productionItem
      .filter((itm) => !itm.secondaryProduct)
      .map((itm) => {
        return {
          ...defaultDocumentLine,
          id: itm.id,
          itemCode: itm.itemCode,
          itemName: itm.item?.itemName,
          qty: Math.abs(itm.quantity || ''),
          whsCode: itm.whsCode,
          weight: itm.weight || '',
          quantityUnitId: itm.item?.quantityUnit?.id,
          quantityUnitCode: itm.item?.quantityUnit?.code,
          quantityUnitName: itm.item?.quantityUnit?.name,
          baseId: selectedValues.id,
          baseLine: itm.id,
          baseDocnum: selectedValues.workingNumber,
          baseDocType: null,
        };
      });

    setDocLines(newDocLines);
  };

  const buildReturnLines = () => {
    const issues = selectedValues?.issues ?? [];
    if (!issues.length) {
      setDocLines([]);
      return addNotification({
        content: 'Nincs kiadott tétel!',
        type: NotificationType.WARNING,
      });
    }

    const returns = selectedValues?.returns ?? [];
    const productionItems = selectedValues?.productionItem ?? [];

    const addToMap = (map, line, field) => {
      const itemCode = line.itemCode || line.item?.itemCode || line.item?.code;
      if (!itemCode) {
        return map;
      }
      const isBatch = line.item?.invManagementMethod === InvManagementMethod.BATCH;
      if (!map[itemCode]) {
        map[itemCode] = {
          itemCode,
          itemName: line.itemName || line.item?.itemName || '',
          quantityUnitId: line.quantityUnitId || line.item?.quantityUnit?.id,
          quantityUnitCode: line.quantityUnitCode || line.item?.quantityUnit?.code,
          quantityUnitName: line.quantityUnitName || line.item?.quantityUnit?.name,
          whsCode: line.whsCode || selectedValues?.whsCode,
          isBatchManaged: isBatch,
          issuedQty: 0,
          returnedQty: 0,
        };
      }
      const qty = isBatch ? line.batchQty ?? line.qty ?? line.quantity ?? 0 : line.qty ?? line.quantity ?? 0;
      map[itemCode][field] += Math.abs(toNumber(qty));
      return map;
    };

    const byItem = issues.reduce((acc, line) => addToMap(acc, line, 'issuedQty'), {});
    returns.reduce((acc, line) => addToMap(acc, line, 'returnedQty'), byItem);

    const newDocLines = Object.values(byItem)
      .map((line) => {
        const returnableQty = Math.max(0, line.issuedQty - line.returnedQty);
        if (returnableQty <= 0) {
          return null;
        }
        const prodItem = productionItems.find((itm) => itm.itemCode === line.itemCode);
        return {
          ...defaultDocumentLine,
          id: prodItem?.id ?? line.itemCode,
          itemCode: line.itemCode,
          itemName: line.itemName,
          qty: returnableQty,
          issuedQty: line.issuedQty,
          returnableQty,
          whsCode: line.whsCode || prodItem?.whsCode,
          weight: prodItem?.weight || '',
          quantityUnitId: line.quantityUnitId || prodItem?.item?.quantityUnit?.id,
          quantityUnitCode: line.quantityUnitCode || prodItem?.item?.quantityUnit?.code,
          quantityUnitName: line.quantityUnitName || prodItem?.item?.quantityUnit?.name,
          baseId: selectedValues.id,
          baseLine: prodItem?.id ?? null,
          baseDocnum: selectedValues.workingNumber,
          baseDocType: null,
        };
      })
      .filter(Boolean);

    setDocLines(newDocLines);
  };

  const setQty = () => {
    if (isIncoming) {
      return buildReturnLines();
    }
    return buildIssueLines();
  };

  useEffect(() => {
    if (open) {
      setQty();
    } else {
      setDocLines([]);
      setBinManagedByWhs({});
      setBatchLine(null);
      setReturnLine(null);
      setTargetWhsCode(null);
      setTargetBinId(null);
    }
  }, [open, direction, selectedValues]);

  useEffect(() => {
    if (!open || !isIncoming) return;
    const defaultWhs = selectedValues?.whsCode ?? null;
    setTargetWhsCode(defaultWhs);

    const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === defaultWhs);
    if (whs?.binManaged && whs?.dispatchBin) {
      setTargetBinId(whs.dispatchBin ?? null);
    } else {
      setTargetBinId(null);
    }
  }, [open, isIncoming, selectedValues, autocompleteData]);

  useEffect(() => {
    if (!open || docLines.length === 0) return;
    const whsCodes = [...new Set(docLines.map((line) => line.whsCode).filter(Boolean))];
    if (whsCodes.length === 0) return;

    let active = true;
    Promise.all(
      whsCodes.map((code) =>
        warehouseService
          .getBins(code)
          .then((data) => [code, (data ?? []).length > 0])
          .catch(() => [code, false])
      )
    ).then((entries) => {
      if (!active) return;
      const next = entries.reduce((acc, [code, hasBins]) => {
        acc[code] = hasBins;
        return acc;
      }, {});
      setBinManagedByWhs(next);
    });

    return () => {
      active = false;
    };
  }, [open, docLines]);

  const handleQuantityChange = (id, value) => {
    const parsed = parseFloat(value);
    setDocLines((prev) =>
      prev.map((line) => {
        if (line.id !== id) {
          return line;
        }
        if (Number.isNaN(parsed)) {
          return { ...line, qty: '' };
        }
        const maxQty = typeof line.returnableQty === 'number' ? line.returnableQty : null;
        const nextQty = maxQty !== null ? Math.min(parsed, maxQty) : parsed;
        return { ...line, qty: nextQty };
      })
    );
  };

  const handleCloseBatchDialog = () => {
    setBatchLine(null);
  };

  const handleCloseReturnDialog = () => {
    setReturnLine(null);
  };

  const handlePostDocument = () => {
    if (isIncoming) {
      if (!targetWhsCode) {
        addNotification({
          content: 'Nincs megadott fogadó raktár!',
          type: NotificationType.WARNING,
        });
        return;
      }

      const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === targetWhsCode);
      if (whs?.binManaged && !targetBinId) {
        addNotification({
          content: 'Nincs megadott fogadó rakhely!',
          type: NotificationType.WARNING,
        });
        return;
      }
    }

    const targetWhs = isIncoming
      ? autocompleteData?.warehouse?.find((warehouse) => warehouse.code === targetWhsCode)
      : null;
    const targetBin = targetWhs?.bin?.find((bin) => bin.binId === targetBinId) ?? null;
    const isTargetBinManaged = Boolean(targetWhs?.binManaged);

    const document = {
      ...cleanValues(defaultValues),
      ...(isIncoming ? { whsCode: targetWhsCode } : {}),
      docLine: docLines
        .filter((l) => l.qty > 0)
        .map((line) => ({
          ...cleanValues(line),
          ...(isIncoming
            ? {
                whsCode: targetWhsCode,
              }
            : {}),
          docLineBatchSn: line.docLineBatchSn.map((batch) => ({
            ...cleanValues(batch),
          })),
          docLineBin: isIncoming
            ? isTargetBinManaged && targetBin
              ? line.docLineBatchSn?.length
                ? line.docLineBatchSn.map((batch) => ({
                    id: uuidv4(),
                    binCode: targetBin.binCode ?? '',
                    binId: targetBin.binId ?? null,
                    snBinId: batch.id ?? null,
                    quantity: batch.quantity ?? 0,
                  }))
                : [
                    {
                      id: uuidv4(),
                      binCode: targetBin.binCode ?? '',
                      binId: targetBin.binId ?? null,
                      snBinId: null,
                      quantity: line.qty ?? 0,
                    },
                  ]
              : []
            : line.docLineBin.map((bin) => ({
                ...cleanValues(bin),
              })),
        })),
    };

    const postRequest = isIncoming
      ? productionService.createProductionReturn(document, selectedValues.id)
      : productionService.createProductionIssue(document, selectedValues.id);

    postRequest.then((data) => {
      onClose();
      setRefetchTable(true);
      setSelectedValues(data);
    });
  };

  const handleBatchSave = (dl) => {
    setDocLines((prev) =>
      prev.map((line) =>
        line.id === batchLine.id
          ? {
              ...line,
              docLineBatchSn: dl?.docLineBatchSn ?? [],
              docLineBin: dl?.docLineBin ?? [],
            }
          : line
      )
    );
  };

  const handleReturnSave = (payload) => {
    if (!returnLine || typeof returnLine.selectedLineIndex !== 'number') return;
    setDocLines((prev) =>
      prev.map((line, idx) =>
        idx === returnLine.selectedLineIndex
          ? {
              ...line,
              docLineBatchSn: payload?.docLineBatchSn ?? [],
              qty: typeof payload?.totalQty === 'number' ? payload.totalQty : line.qty,
            }
          : line
      )
    );
  };

  return (
    <DialogUI open={open} onClose={onClose} headerContent={dialogTitle} maxWidth="lg" fullWidth>
      <Table style={{ marginTop: 16 }}>
        <TableHead sx={{ background: ui.tableHeader }}>
          <TableRow>
            <TableCell>Cikkszám</TableCell>
            <TableCell>Megnevezés</TableCell>
            <TableCell>Mennyiség</TableCell>
            {isIncoming ? <TableCell>Kiadott</TableCell> : null}
            {isIncoming ? <TableCell>Max visszavét</TableCell> : null}
            <TableCell>Me.</TableCell>
            <TableCell>Sarzs/Rakhely</TableCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {docLines.map((itm, idx) => (
            <TableRow key={itm.id}>
              <TableCell>{itm.itemCode}</TableCell>
              <TableCell>{itm.itemName}</TableCell>
              <TableCell style={{ maxWidth: 120 }}>
                <InputUI
                  type="number"
                  value={itm.qty}
                  onChange={(e) => handleQuantityChange(itm.id, e.target.value)}
                  inputProps={{
                    min: 0,
                    ...(isIncoming && typeof itm.returnableQty === 'number' ? { max: itm.returnableQty } : {}),
                  }}
                  size="small"
                />
              </TableCell>
              {isIncoming ? <TableCell>{itm.issuedQty}</TableCell> : null}
              {isIncoming ? <TableCell>{itm.returnableQty}</TableCell> : null}
              <TableCell>{itm.quantityUnitCode}</TableCell>
              <TableCell>
                {(() => {
                  const item = selectedValues?.productionItem?.find((i) => i.itemCode === itm.itemCode);
                  const isBatchManaged = item && item.item?.invManagementMethod === InvManagementMethod.BATCH;
                  const isBinManaged = Boolean(binManagedByWhs[itm.whsCode]);
                  if (isIncoming && isBatchManaged) {
                    return (
                      <IconButton
                        onClick={() => {
                          setReturnLine({
                            ...itm,
                            selectedLineIndex: idx,
                          });
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    );
                  }
                  if (!isIncoming && (isBatchManaged || isBinManaged)) {
                    return (
                      <IconButton
                        onClick={() => {
                          setBatchLine({
                            ...itm,
                            selectedLineIndex: idx,
                            isBatchManaged,
                          });
                        }}
                        size="small"
                      >
                        <EditIcon />
                      </IconButton>
                    );
                  }
                  return null;
                })()}
              </TableCell>
            </TableRow>
          ))}
        </TableBody>
      </Table>

      {batchLine && (
        <ProductionBatchOutDialog
          open={!!batchLine}
          onClose={handleCloseBatchDialog}
          selectedLine={batchLine}
          whsCode={batchLine?.whsCode ?? selectedValues?.whsCode}
          defaultBatchLine={defaultBatchLine}
          onSave={(dl) => handleBatchSave(dl)}
        />
      )}

      {returnLine && (
        <ProductionReturnDialog
          open={!!returnLine}
          onClose={handleCloseReturnDialog}
          selectedLine={returnLine}
          prodId={selectedValues?.id}
          onSave={handleReturnSave}
        />
      )}

      {isIncoming && (
        <Box sx={{ px: 4, mt: 4, display: 'grid', gridTemplateColumns: 'repeat(2, minmax(0, 1fr))', gap: 3 }}>
          <AutoCompleteSelectUI
            label={<FormLabelUI text="Fogadó raktár" />}
            InputLabelProps={{ shrink: Boolean(targetWhsCode) }}
            selectedValue={targetWhsCode}
            selectedLabelValue={autocompleteOptions?.warehouse?.find((w) => w.value === targetWhsCode)?.label ?? ''}
            onChange={(_e, newVal) => {
              const nextWhsCode = newVal?.value ?? null;
              setTargetWhsCode(nextWhsCode);

              const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === nextWhsCode);
              if (whs?.binManaged && whs?.dispatchBin) {
                setTargetBinId(whs.dispatchBin ?? null);
              } else {
                setTargetBinId(null);
              }
            }}
            dataset={autocompleteOptions?.warehouse ?? []}
          />
          {(() => {
            const whs = autocompleteData?.warehouse?.find((warehouse) => warehouse.code === targetWhsCode);
            const isBinManaged = Boolean(whs?.binManaged);
            if (!isBinManaged) return null;
            const binOptions = generateAutocompleteItems(whs?.bin ?? [], 'binCode', 'binId');
            const selectedBin = (whs?.bin ?? []).find((bin) => bin.binId === targetBinId);

            return (
              <AutoCompleteSelectUI
                label={<FormLabelUI text="Fogado rakhely" />}
                InputLabelProps={{ shrink: Boolean(targetBinId) }}
                selectedValue={targetBinId}
                selectedLabelValue={selectedBin?.binCode ?? ''}
                onChange={(_e, newVal) => {
                  setTargetBinId(newVal?.value ?? null);
                }}
                dataset={binOptions}
              />
            );
          })()}
        </Box>
      )}

      <Box sx={{ px: 4, mt: 8, display: 'flex', justifyContent: 'center', gap: 3 }}>
        <ButtonUI
          text={'Könyvelés'}
          className="bg-success"
          type="submit"
          onClick={() => handlePostDocument()}
        ></ButtonUI>
        <ButtonUI
          text="Mégse"
          className="bg-"
          color="blue-gray"
          type="button"
          variant="gradient"
          onClick={onClose}
        ></ButtonUI>
      </Box>
    </DialogUI>
  );
};

export default ProductionIssueDialog;
